import { useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { format, parseISO } from 'date-fns'
import {
  CalendarDays,
  CloudUpload,
  FileStack,
  FileText,
  Link as LinkIcon,
  Loader2,
  TextSearch,
  Trash2,
} from 'lucide-react'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from 'react'
import { toast } from 'sonner'

import { FilePondUpload } from '@/components/file-pond-upload'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import MultipleSelector, { type Option as MultipleSelectorOption } from '@/components/ui/multiple-selector'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { useAuth } from '@/features/authentication/components/auth-provider'
import { ConfirmAlert } from '@/components/confirm-alert'
import { isAdminUser } from '@/lib/role-guards'
import { cn } from '@/lib/utils'
import {
  useCreatePastResource,
  useListAllPastResource,
  useDeletePastResource,
  useUpdatePastResource,
} from '@/utils/api/membership-service'
import type { ListParams } from '@/utils/api/membership-service/models/list-params'
import { CpdEventSelect, type CpdEventOption } from './components/CpdEventSelect'
import { InlineDocumentViewer } from './components/InlineDocumentViewer'
import { DOCUMENT_MIME_TYPES } from './constants'
import type {
  PastCpdResourceCreate,
  PastCpdResourceListItem,
  PastCpdResourceUpdate,
} from '@/utils/api/membership-service/models'
// import type { DocumentItem } from './types'
const CATEGORY_ALL = 'all' as const
const STATUS_ALL = 'all' as const
const ALLOWED_FILE_TYPES = [
  DOCUMENT_MIME_TYPES.PDF,
  DOCUMENT_MIME_TYPES.DOC,
  DOCUMENT_MIME_TYPES.DOCX,
  DOCUMENT_MIME_TYPES.XLS,
  DOCUMENT_MIME_TYPES.XLSX,
  DOCUMENT_MIME_TYPES.CSV,
  DOCUMENT_MIME_TYPES.PPT,
  DOCUMENT_MIME_TYPES.PPTX,
  DOCUMENT_MIME_TYPES.PNG,
  DOCUMENT_MIME_TYPES.JPEG,
  DOCUMENT_MIME_TYPES.GIF,
  DOCUMENT_MIME_TYPES.WEBP,
  DOCUMENT_MIME_TYPES.SVG,
]

const DEFAULT_CATEGORY = 'General'
const DEFAULT_PAGE_SIZE = 9
const BASE_PAST_RESOURCE_STATUSES = ['all', 'draft', 'published', 'archived'] as const
const CATEGORY_PRESETS: string[] = [
  DEFAULT_CATEGORY,
  'Membership Governance',
  'Governance & Policies',
  'Professional Standards',
  'Operations & Support',
  'Templates & Forms',
  'Communications & Media',
  'Onboarding & Support',
]

type PastResourceStatus = (typeof BASE_PAST_RESOURCE_STATUSES)[number]
type StatusFilterValue = PastResourceStatus | typeof STATUS_ALL

type UploadedFileMeta = {
  id: string
  url: string
  name: string
  size?: number
  type?: string
}

type ExistingFileInfo = {
  label: string
  details?: string
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function highlightMatch(text: string, query: string): ReactNode {
  if (!query) return text
  const parts = text.split(new RegExp(`(${escapeRegExp(query)})`, 'gi'))
  const loweredQuery = query.toLowerCase()
  return parts.map((part, index) =>
    part.toLowerCase() === loweredQuery ? (
      <mark key={index} className="rounded-sm bg-yellow-200 px-0.5 py-0.5 text-foreground">
        {part}
      </mark>
    ) : (
      <span key={index}>{part}</span>
    ),
  )
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return ''
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let size = bytes
  let unitIndex = 0
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex += 1
  }
  const precision = size >= 10 || unitIndex === 0 ? 0 : 1
  return `${size.toFixed(precision)} ${units[unitIndex]}`
}

function buildPageNumberList(current: number, total: number): number[] {
  if (!Number.isFinite(total) || total <= 1) {
    return [0]
  }
  const pages = new Set<number>([0, total - 1, current])
  const neighbors = [current - 2, current - 1, current + 1, current + 2]
  neighbors.forEach((page) => {
    if (page > 0 && page < total - 1) {
      pages.add(page)
    }
  })
  const pageArray = Array.from(pages).filter((page) => page >= 0 && page < total)
  pageArray.sort((a, b) => a - b)
  return pageArray
}

export function MembershipDocumentsPage() {
  const { user } = useAuth()
  const isAdmin = isAdminUser(user)

  const [searchTerm, setSearchTerm] = useState('')
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false)
  const [categoryFilter, setCategoryFilter] = useState<string>(CATEGORY_ALL)
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>(() => (isAdmin ? STATUS_ALL : 'published'))
  const [selectedDocument, setSelectedDocument] = useState<PastCpdResourceListItem | null>(null)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create')
  const [editingDocument, setEditingDocument] = useState<PastCpdResourceListItem | null>(null)
  const [isFullscreenActive, setIsFullscreenActive] = useState(false)
  const viewerRef = useRef<HTMLDivElement | null>(null)
  const pendingFileTypeRef = useRef<string | null>(null)
  const [formTitle, setFormTitle] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [formCategory, setFormCategory] = useState<string>('')
  const [formStatus, setFormStatus] = useState<'draft' | 'published' | 'archived' | 'all'>('draft')
  const [formTags, setFormTags] = useState<MultipleSelectorOption[]>([])
  const [formDocumentKind, setFormDocumentKind] = useState<'file' | 'link'>('file')
  const [formUploadedFile, setFormUploadedFile] = useState<UploadedFileMeta | null>(null)
  const [formExternalLink, setFormExternalLink] = useState('')
  const [formCpdEvent, setFormCpdEvent] = useState<CpdEventOption | null>(null)
  const [pageIndex, setPageIndex] = useState(0)
  const pageSize = DEFAULT_PAGE_SIZE
  const [existingFileInfo, setExistingFileInfo] = useState<ExistingFileInfo | null>(null)
  const isEditing = dialogMode === 'edit' && editingDocument !== null

  useEffect(() => {
    setStatusFilter(isAdmin ? STATUS_ALL : 'published')
  }, [isAdmin])

  useEffect(() => {
    setPageIndex(0)
  }, [statusFilter, isAdmin])

  const effectiveStatusFilter: StatusFilterValue = isAdmin ? statusFilter : 'published'

  const listParams = useMemo<ListParams>(() => {
    const params: ListParams = {
      page: pageIndex,
      size: pageSize,
      status: effectiveStatusFilter,
    }
    return params
  }, [effectiveStatusFilter, pageIndex, pageSize])

  const queryClient = useQueryClient()
  const pastResourcesQuery = useListAllPastResource(listParams, {
    query: {
      refetchOnWindowFocus: false,
    },
  })
  const pastResourcesResponse = pastResourcesQuery.data
  const pastResourcesData = pastResourcesResponse?.data
  // const pastResourcesData = useMemo(
  //   () => extractPastCpdResources(pastResourcesApiResponse),
  //   [pastResourcesApiResponse],
  // )
  const pastResources = pastResourcesData?.content ?? []
  // const availableStatuses = pastResourcesData?.statuses ?? []
  const totalItems = pastResourcesData?.totalElements ?? 0
  const totalPagesFromApi = pastResourcesData?.totalPages ?? 0
  const isDocumentsLoading = pastResourcesQuery.isLoading
  const isDocumentsFetching = pastResourcesQuery.isFetching && !pastResourcesQuery.isLoading
  const isDocumentsError =
    pastResourcesQuery.isError
  const refetchDocuments = pastResourcesQuery.refetch
  const pastResourcesQueryKey = pastResourcesQuery.queryKey
  const totalPages = useMemo(() => {
    if (totalPagesFromApi && totalPagesFromApi > 0) {
      return totalPagesFromApi
    }
    if (totalItems && totalItems > 0) {
      return Math.max(1, Math.ceil(totalItems / pageSize))
    }
    if (!pastResources.length) {
      return Math.max(pageIndex + 1, 1)
    }
    if (pastResources.length < pageSize) {
      return Math.max(pageIndex + 1, 1)
    }
    return pageIndex + 2
  }, [pageIndex, pageSize, pastResources.length, totalItems, totalPagesFromApi])
  const pageNumbers = useMemo(() => buildPageNumberList(pageIndex, totalPages), [pageIndex, totalPages])
  const pageRange = useMemo(() => {
    if (!pastResources.length) {
      return { start: 0, end: 0 }
    }
    const start = pageIndex * pageSize + 1
    let end = start + pastResources.length - 1
    if (totalItems > 0) {
      end = Math.min(end, totalItems)
    }
    return { start, end }
  }, [pageIndex, pageSize, pastResources.length, totalItems])

  const categories = useMemo(() => {
    const unique = new Set<string>(CATEGORY_PRESETS)
    pastResources.forEach((document) => {
      if (document?.category) unique.add(document.category)
    })
    if (!unique.size) {
      unique.add(DEFAULT_CATEGORY)
    }
    return Array.from(unique)
  }, [pastResources])

  const filterStatusOptions = useMemo<StatusFilterValue[]>(() => {
    const options: StatusFilterValue[] = []
    if (isAdmin) {
      options.push(STATUS_ALL)
    }
    const addStatus = (status?: PastResourceStatus) => {
      if (!status) return
      if (!options.includes(status)) {
        options.push(status)
      }
    }
    BASE_PAST_RESOURCE_STATUSES.forEach(addStatus)
    // availableStatuses.forEach(addStatus)
    // documents.forEach((document) => {
    //   if (document.status) {
    //     addStatus(document.status)
    //   }
    // })
    return options
  }, [pastResources, isAdmin])

  const statusOptions = useMemo<PastResourceStatus[]>(() => {
    const unique = new Set<PastResourceStatus>(BASE_PAST_RESOURCE_STATUSES)
    // pastResources.forEach((document) => {
    //   if (document.status) unique.add(document.status)
    // })
    return Array.from(unique)
  }, [pastResources])

  const tagOptionsInitial = useMemo<MultipleSelectorOption[]>(() => {
    const seen = new Set<string>()
    const options: MultipleSelectorOption[] = []
    pastResources.forEach((document) => {
      document?.tags?.forEach((tag) => {
        if (!seen.has(tag)) {
          seen.add(tag)
          options.push({ value: tag, label: tag })
        }
      })
    })
    return options
  }, [pastResources])
  const [tagOptions, setTagOptions] = useState<MultipleSelectorOption[]>([])
  const [deletingId, setDeletingId] = useState<number | null>(null)

  useEffect(() => {
    setTagOptions((current) => {
      if (!tagOptionsInitial.length && !current.length) {
        return []
      }
      const existingValues = new Set(current.map((option) => option.value))
      const merged = [...current]
      tagOptionsInitial.forEach((option) => {
        if (!existingValues.has(option.value)) {
          merged.push(option)
          existingValues.add(option.value)
        }
      })
      return merged
    })
  }, [tagOptionsInitial])

  useEffect(() => {
    if (!formCategory && categories.length) {
      setFormCategory(categories[0])
    }
  }, [categories, formCategory])

  useEffect(() => {
    if (statusOptions.length && !statusOptions.includes(formStatus)) {
      setFormStatus(statusOptions[0])
    }
  }, [formStatus, statusOptions])


  useEffect(() => {
    if (formDocumentKind === 'file') {
      setFormExternalLink('')
      setIsSubmitAttempted(false)
      pendingFileTypeRef.current = null
      return
    }

    setFormUploadedFile(null)
    setExistingFileInfo(null)
    pendingFileTypeRef.current = null
    setIsSubmitAttempted(false)
  }, [formDocumentKind])

  useEffect(() => {
    if (!uploadDialogOpen) {
      setIsSubmitAttempted(false)
    }
  }, [uploadDialogOpen])

  const resetForm = useCallback(() => {
    setFormTitle('')
    setFormDescription('')
    setFormCategory(categories[0] ?? DEFAULT_CATEGORY)
    setFormStatus(statusOptions[0] ?? 'draft')
    setFormTags([])
    setFormDocumentKind('file')
    setFormUploadedFile(null)
    setFormExternalLink('')
    setFormCpdEvent(null)
    setIsSubmitAttempted(false)
    pendingFileTypeRef.current = null
    setExistingFileInfo(null)
  }, [categories, statusOptions])

  const handleUploadDialogOpenChange = useCallback(
    (open: boolean) => {
      setUploadDialogOpen(open)
      if (!open) {
        resetForm()
        setDialogMode('create')
        setEditingDocument(null)
      } else {
        setIsSubmitAttempted(false)
      }
    },
    [resetForm],
  )

  const handleMutationSuccess = useCallback(
    async ({ toastMessage, resetPageIndex }: { toastMessage: string; resetPageIndex?: boolean }) => {
      if (resetPageIndex) {
        setPageIndex(0)
      }
      if (pastResourcesQueryKey) {
        await queryClient.invalidateQueries({ queryKey: pastResourcesQueryKey })
      } else {
        await queryClient.invalidateQueries()
      }
      toast.success(toastMessage)
      resetForm()
      setDialogMode('create')
      setEditingDocument(null)
      setUploadDialogOpen(false)
    },
    [pastResourcesQueryKey, queryClient, resetForm],
  )

  const handleMutationError = useCallback(
    (error: AxiosError<{ message?: string }> | Error, fallbackMessage: string) => {
      const axiosError = error as AxiosError<{ message?: string }>
      const message =
        axiosError.response?.data?.message ??
        (axiosError.message || (error instanceof Error ? error.message : fallbackMessage))
      toast.error(message ?? fallbackMessage)
    },
    [],
  )

  const createPastResourceMutation = useCreatePastResource({
    mutation: {
      onSuccess: async () => {
        await handleMutationSuccess({ toastMessage: 'Document uploaded successfully', resetPageIndex: true })
      },
      onError: (error: AxiosError<{ message?: string }> | Error) => {
        handleMutationError(error, 'Failed to upload document')
      },
    },
  })

  const deletePastResourceMutation = useDeletePastResource(
    {
      mutation: {
        onError: (error: AxiosError<{ message?: string }> | Error) => {
          handleMutationError(error, 'Failed to delete document')
        },
      },
    },
    queryClient,
  )

  const updatePastResourceMutation = useUpdatePastResource({
    mutation: {
      onSuccess: async () => {
        await handleMutationSuccess({ toastMessage: 'Document updated successfully' })
      },
      onError: (error: AxiosError<{ message?: string }> | Error) => {
        handleMutationError(error, 'Failed to update document')
      },
    },
  })

  const handleTagSearchSync = useCallback(
    (value: string) => {
      const query = value.trim().toLowerCase()
      if (!query) return tagOptions
      return tagOptions.filter((option) => option.label.toLowerCase().includes(query))
    },
    [tagOptions],
  )

  const handleTagsChange = useCallback(
    (options: MultipleSelectorOption[]) => {
      setFormTags(options)
      setTagOptions((current) => {
        const missing = options.filter(
          (option) => !current.some((existing) => existing.value === option.value),
        )
        return missing.length ? [...current, ...missing] : current
      })
    },
    [setFormTags, setTagOptions],
  )

  const handleCreateDocumentClick = useCallback(() => {
    setDialogMode('create')
    setEditingDocument(null)
    resetForm()
  }, [resetForm])

  const handleEditDocument = useCallback(
    (document: PastCpdResourceListItem) => {
      if (!isAdmin) return

      setDialogMode('edit')
      setEditingDocument(document)

      setFormTitle(document.title ?? '')
      setFormDescription(document.description ?? '')

      const nextCategory = document.category ?? categories[0] ?? DEFAULT_CATEGORY
      setFormCategory(nextCategory)

      const normalizedStatus = document.status?.toLowerCase() as PastResourceStatus | undefined
      if (normalizedStatus && statusOptions.includes(normalizedStatus)) {
        setFormStatus(normalizedStatus)
      } else if (statusOptions.includes('draft')) {
        setFormStatus('draft')
      } else if (statusOptions.length) {
        setFormStatus(statusOptions[0])
      }

      const documentTags = document.tags ?? []
      const tagOptionsForDocument = documentTags.map((tag) => ({ value: tag, label: tag }))
      setFormTags(tagOptionsForDocument)
      if (tagOptionsForDocument.length) {
        setTagOptions((current) => {
          const existing = new Set(current.map((option) => option.value))
          let hasChange = false
          const updated = [...current]
          tagOptionsForDocument.forEach((option) => {
            if (!existing.has(option.value)) {
              updated.push(option)
              existing.add(option.value)
              hasChange = true
            }
          })
          return hasChange ? updated : current
        })
      }

      const documentKind = document.type === 'link' ? 'link' : 'file'
      setFormDocumentKind(documentKind)
      if (documentKind === 'link') {
        setFormExternalLink(document.preview?.url ?? '')
      } else {
        setFormExternalLink('')
      }

      if (document.cpdEventId && document.cpdEventName) {
        setFormCpdEvent({ id: document.cpdEventId, name: document.cpdEventName })
      } else {
        setFormCpdEvent(null)
      }

      if (documentKind === 'file') {
        setExistingFileInfo({
          label: document.preview?.label ?? document.title ?? 'Attached file',
          details: document.size ?? undefined,
        })
      } else {
        setExistingFileInfo(null)
      }

      setFormUploadedFile(null)
      setIsSubmitAttempted(false)
      pendingFileTypeRef.current = null
      setUploadDialogOpen(true)
    },
    [categories, isAdmin, setTagOptions, statusOptions],
  )

  const handleRemoveAttachedFile = useCallback(() => {
    setFormUploadedFile(null)
    setExistingFileInfo(null)
    setIsSubmitAttempted(false)
    pendingFileTypeRef.current = null
  }, [])

  const visibleDocuments = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase()

    return pastResources.filter((document) => {
      // Skip null/undefined documents from API response
      if (!document) {
        return false
      }

      const matchesCategory =
        categoryFilter === CATEGORY_ALL || document.category === categoryFilter
      const matchesStatus =
        effectiveStatusFilter === STATUS_ALL ||
        (document.status ?? STATUS_ALL) === effectiveStatusFilter

      const matchesSearch =
        !normalizedSearch ||
        document.title?.toLowerCase().includes(normalizedSearch) ||
        document.description?.toLowerCase().includes(normalizedSearch) ||
        document.cpdEventName?.toLowerCase().includes(normalizedSearch) ||
        document.tags?.some((tag) => tag.toLowerCase().includes(normalizedSearch))

      return matchesCategory && matchesStatus && matchesSearch
    })
  }, [categoryFilter, pastResources, effectiveStatusFilter, searchTerm])

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  }

  const highlightQuery = searchTerm.trim()

  const isLinkSubmission = formDocumentKind === 'link'
  const isFileSubmission = formDocumentKind === 'file'
  const isLinkMissing = isLinkSubmission && !formExternalLink.trim()
  const isFileMissing = isFileSubmission && !formUploadedFile && !existingFileInfo
  const isSubmitting = createPastResourceMutation.isPending || updatePastResourceMutation.isPending
  const hasAttachedFile = isFileSubmission && Boolean(formUploadedFile || existingFileInfo)
  const attachedFileLabel = formUploadedFile?.name ?? existingFileInfo?.label ?? ''
  const attachedFileDetails = formUploadedFile?.size
    ? formatFileSize(formUploadedFile.size)
    : existingFileInfo?.details

  const handleSubmit = useCallback(async () => {
    if (!isAdmin) {
      return
    }

    setIsSubmitAttempted(true)

    if (!formTitle.trim()) {
      toast.error('Document title is required')
      return
    }

    if (isLinkMissing) {
      toast.error('Portal link is required for link documents')
      return
    }

    if (isFileMissing) {
      toast.error('Upload attachment is required for file documents')
      return
    }

    const payload: PastCpdResourceCreate = {
      title: formTitle.trim(),
      description: formDescription.trim() || undefined,
      category: formCategory || undefined,
      status: formStatus || undefined,
      tags: formTags.length ? formTags.map((tag) => tag.value) : undefined,
      documentType: formDocumentKind,
      externalLink: isLinkSubmission ? formExternalLink.trim() : undefined,
      cpdEvent: formCpdEvent ? { id: formCpdEvent.id, name: formCpdEvent.name } : undefined,
      file:
        isFileSubmission && formUploadedFile
          ? {
            id: formUploadedFile.id,
            url: formUploadedFile.url,
            name: formUploadedFile.name,
            size: formUploadedFile.size,
            type: formUploadedFile.type,
          }
          : undefined,
    }

    try {
      if (isEditing && editingDocument?.id != null) {
        await updatePastResourceMutation.mutateAsync({
          resourceId: editingDocument.id,
          data: payload as PastCpdResourceUpdate,
        })
      } else {
        await createPastResourceMutation.mutateAsync({ data: payload })
      }
    } catch {
      // Error is handled via mutation onError callback
    }
  }, [
    isAdmin,
    formCategory,
    formDescription,
    formDocumentKind,
    formCpdEvent,
    formExternalLink,
    formUploadedFile,
    formStatus,
    formTags,
    formTitle,
    isFileMissing,
    isLinkMissing,
    isFileSubmission,
    isLinkSubmission,
    isEditing,
    editingDocument,
    createPastResourceMutation,
    updatePastResourceMutation,
  ])

  const openDocument = (doc: PastCpdResourceListItem) => {
    if (typeof document !== 'undefined' && document.fullscreenElement && document.exitFullscreen) {
      void document.exitFullscreen().catch(() => {
        /* ignore */
      })
    }
    setSelectedDocument(doc)
    setIsFullscreenActive(false)
  }

  const closeDocument = () => {
    if (typeof document !== 'undefined' && document.fullscreenElement && document.exitFullscreen) {
      void document.exitFullscreen().catch(() => {
        /* ignore */
      })
    }
    setSelectedDocument(null)
    setIsFullscreenActive(false)
  }

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (typeof document === 'undefined') {
        return
      }
      const isActive = document.fullscreenElement === viewerRef.current
      setIsFullscreenActive(isActive)
    }

    typeof document !== 'undefined' && document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      typeof document !== 'undefined' &&
        document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  const formatUpdatedAt = (value: string) => {
    try {
      const parsed = parseISO(value)
      return format(parsed, 'MMM d, yyyy')
    } catch {
      return value
    }
  }

  const handleDeleteDocument = useCallback(
    async (doc: PastCpdResourceListItem) => {
      const id = doc.id
      if (typeof id !== 'number') {
        toast.error('Unable to delete: missing document id')
        return
      }

      setDeletingId(id)
      try {
        await deletePastResourceMutation.mutateAsync({ resourceId: id })
        if (pastResourcesQueryKey) {
          await queryClient.invalidateQueries({ queryKey: pastResourcesQueryKey })
        } else {
          await queryClient.invalidateQueries()
        }
        if (selectedDocument?.id === id) {
          closeDocument()
        }
        setPageIndex(0)
        toast.success('Document deleted')
      } catch (error) {
        handleMutationError(error as AxiosError<{ message?: string }>, 'Failed to delete document')
      } finally {
        setDeletingId(null)
      }
    },
    [
      closeDocument,
      deletePastResourceMutation,
      handleMutationError,
      pastResourcesQueryKey,
      queryClient,
      selectedDocument,
      setPageIndex,
    ],
  )

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Essential Documents Library</h1>
          <p className="text-muted-foreground">
            Access the latest membership agreements, bylaws, and operational guides curated by the UIBFS
            administration.
          </p>
        </div>
        {isAdmin && (
          <Dialog open={uploadDialogOpen} onOpenChange={handleUploadDialogOpenChange}>
            <DialogTrigger asChild>
              <Button onClick={handleCreateDocumentClick}>
                <CloudUpload className="mr-2 h-4 w-4" />
                Upload document
              </Button>
            </DialogTrigger>
            <DialogContent className="flex max-h-[95vh] w-full max-w-2xl flex-col overflow-hidden sm:max-w-xl">
              <DialogHeader className="shrink-0">
                <DialogTitle>{isEditing ? 'Edit document' : 'Upload an essential document'}</DialogTitle>
                <DialogDescription>
                  {isEditing
                    ? 'Update the title, status, or linked assets for this resource.'
                    : 'Draft a resource and control who sees it. Changes publish when you save.'}
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto min-h-0">
                <div className="space-y-5 pr-1 sm:pr-2">
                  <div className="grid gap-2">
                    <label className="text-sm font-medium" htmlFor="document-title">
                      Document title
                    </label>
                    <Input
                      id="document-title"
                      value={formTitle}
                      onChange={(event) => setFormTitle(event.target.value)}
                      placeholder="e.g. Annual Membership Agreement"
                    />
                  </div>
                  <div className="grid gap-2">
                    <label className="text-sm font-medium" htmlFor="document-description">
                      Summary
                    </label>
                    <Textarea
                      id="document-description"
                      value={formDescription}
                      onChange={(event) => setFormDescription(event.target.value)}
                      placeholder="Summarise what members should expect in this document"
                    />
                  </div>
                  <div className="grid gap-2">
                    <label className="text-sm font-medium">Document type</label>
                    <ToggleGroup
                      type="single"
                      variant="outline"
                      className="w-full"
                      value={formDocumentKind}
                      onValueChange={(value) => {
                        if (value === 'file' || value === 'link') {
                          setFormDocumentKind(value)
                        }
                      }}
                    >
                      <ToggleGroupItem value="link" className="flex-1">
                        <LinkIcon className="h-4 w-4 shrink-0" />
                        <span className="truncate">Portal link</span>
                      </ToggleGroupItem>
                      <ToggleGroupItem value="file" className="flex-1">
                        <FileStack className="h-4 w-4 shrink-0" />
                        <span className="truncate">Upload a file</span>
                      </ToggleGroupItem>
                    </ToggleGroup>
                    <p className="text-xs text-muted-foreground">
                      Choose how members will access this document.
                    </p>
                  </div>
                  <div className="grid gap-1 sm:grid-cols-[minmax(0,1.8fr)_minmax(0,0.8fr)_minmax(0,1.2fr)]">
                    <div className="grid gap-2">
                      <label className="text-sm font-medium">Category</label>
                      <Select value={formCategory} onValueChange={setFormCategory}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((category) => (
                            <SelectItem key={category} value={category}>
                              {category}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <label className="text-sm font-medium">Status</label>
                      <Select value={formStatus} onValueChange={(value) => setFormStatus(value as PastResourceStatus)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a status" />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map((statusOption) => (
                            <SelectItem key={statusOption} value={statusOption}>
                              {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <label className="text-sm font-medium">Tags</label>
                    <MultipleSelector
                      options={tagOptions}
                      value={formTags}
                      onChange={handleTagsChange}
                      onSearchSync={handleTagSearchSync}
                      creatable
                      placeholder="Search or add tags"
                      className="w-full"
                      badgeClassName="bg-secondary text-secondary-foreground"
                    />
                    <p className="text-xs text-muted-foreground">
                      Type to search existing tags or press Enter to create a new one.
                    </p>
                  </div>
                  <div className="grid gap-2">
                    <label className="text-sm font-medium" htmlFor="document-cpd-event">
                      Linked CPD event (optional)
                    </label>
                    <CpdEventSelect
                      value={formCpdEvent?.id ?? null}
                      onChange={(option) => setFormCpdEvent(option)}
                      placeholder="Search and link a CPD event"
                      disabled={!isAdmin || isSubmitting}
                    />
                    <p className="text-xs text-muted-foreground">
                      Attach this file to a CPD event so members know where it applies.
                    </p>
                  </div>
                  {isLinkSubmission && (
                    <div className="grid gap-2">
                      <label className="text-sm font-medium" htmlFor="document-link">
                        Portal link 
                      </label>
                      <Input
                        id="document-link"
                        value={formExternalLink}
                        onChange={(event) => setFormExternalLink(event.target.value)}
                        required={isLinkSubmission}
                        aria-required={isLinkSubmission}
                        aria-invalid={isSubmitAttempted && isLinkMissing}
                        placeholder="https://example.com/secure-resource"
                      />
                      {isSubmitAttempted && isLinkMissing ? (
                        <p className="text-xs text-destructive">
                          Add the portal URL to finish this submission.
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          Share the hosted location members should visit.
                        </p>
                      )}
                    </div>
                  )}
                  {isFileSubmission && (
                    <div className="grid gap-2">
                      <div className="flex items-start justify-between gap-2">
                        <label className="text-sm font-medium" htmlFor="document-filepond">
                          Upload attachment
                        </label>
                      </div>
                      {!hasAttachedFile ? (
                        <FilePondUpload
                          accept={ALLOWED_FILE_TYPES}
                          multiple={false}
                          required={isFileSubmission && !isEditing}
                          disabled={!isAdmin || isSubmitting}
                          className="rounded-md border border-dashed border-muted-foreground/40"
                          onAddFile={(file) => {
                            pendingFileTypeRef.current = file.type
                            setFormUploadedFile(null)
                          }}
                          onUploadComplete={(fileId, fileUrl, filename, fileSize) => {
                            const fileType = pendingFileTypeRef.current ?? undefined
                            setFormUploadedFile({
                              id: fileId,
                              url: fileUrl,
                              name: filename,
                              size: fileSize,
                              type: fileType,
                            })
                            setExistingFileInfo(null)
                            pendingFileTypeRef.current = null
                          }}
                          onUploadError={(message) => {
                            pendingFileTypeRef.current = null
                            setFormUploadedFile(null)
                            // eslint-disable-next-line no-console
                            console.error('File upload failed:', message)
                          }}
                          onRemove={() => {
                            pendingFileTypeRef.current = null
                            setFormUploadedFile(null)
                          }}
                        />
                      ) : (
                        <div className="flex items-start justify-between gap-2 rounded-md border border-dashed border-muted-foreground/40 bg-muted/40 p-3 text-sm">
                          <div >
                            <p className="font-medium">{attachedFileLabel}</p>
                            {attachedFileDetails ? (
                              <p className="text-xs text-muted-foreground">{attachedFileDetails}</p>
                            ) : null}
                            {formUploadedFile?.type ? (
                              <p className="text-xs text-muted-foreground">
                                {formUploadedFile.type}
                              </p>
                            ) : null}
                          </div>
                          <div className="ml-auto">
                            <Button
                              type="button"
                              variant="destructive"
                              size="xs"
                              onClick={handleRemoveAttachedFile}
                            >
                              Remove file
                            </Button>
                          </div>
                        </div>
                      )}
                      {isSubmitAttempted && isFileMissing ? (
                        <p className="text-xs text-destructive">
                          Upload the document file before saving.
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          {hasAttachedFile
                            ? 'Remove the current file to upload a replacement.'
                            : 'Accepts PDF, all Microsoft Office formats (DOC, PPT, XLS), and images.'}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter className="shrink-0 flex flex-col gap-3 border-t pt-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col gap-1 text-xs text-muted-foreground overflow-y-auto max-h-20">
                  <div className="flex flex-wrap gap-x-2 gap-y-1">
                    {formStatus && (
                      <span>
                        Status:{' '}
                        <span className="font-medium">
                          {formStatus.charAt(0).toUpperCase() + formStatus.slice(1)}
                        </span>
                      </span>
                    )}
                    {formCategory && (
                      <span>
                        Category:{' '}
                        <span className="font-medium">{formCategory}</span>
                      </span>
                    )}
                    {formTags.length > 0 && (
                      <span>
                        Tags:{' '}
                        <span className="font-medium">
                          {formTags.map((tag) => tag.label).join(', ')}
                        </span>
                      </span>
                    )}
                    {formCpdEvent?.name && (
                      <span>
                        CPD event:{' '}
                        <span className="font-medium">{formCpdEvent.name}</span>
                      </span>
                    )}
                    {isLinkSubmission && formExternalLink && (
                      <span>
                        Link:{' '}
                        <span className="font-medium truncate">{formExternalLink}</span>
                      </span>
                    )}
                    {isFileSubmission && hasAttachedFile && attachedFileLabel && (
                      <span>
                        File:{' '}
                        <span className="font-medium">
                          {attachedFileLabel}
                          {attachedFileDetails ? ` • ${attachedFileDetails}` : ''}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  type="button"
                  className="opacity-90"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>{isEditing ? 'Update document' : `Save ${formStatus ? `as ${formStatus}` : ''}`}</>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card>
        <CardHeader className="gap-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex w-full flex-col gap-4 md:flex-row md:flex-wrap md:items-center">
              <div className="relative flex-1 min-w-[240px]">
                <TextSearch className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Search by title, description, or tags"
                  className="pl-9"
                />
              </div>
              <div className="flex flex-col gap-4 sm:flex-row md:flex-1 md:justify-end">
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-full min-w-[180px] sm:w-48">
                    <SelectValue placeholder="Filter by category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={CATEGORY_ALL}>All categories</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={statusFilter}
                  onValueChange={(value) => setStatusFilter(value as StatusFilterValue)}
                  disabled={!isAdmin}
                >
                  <SelectTrigger className="w-full min-w-[160px] sm:w-44">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    {isAdmin && <SelectItem value={STATUS_ALL}>All statuses</SelectItem>}
                    {filterStatusOptions
                      .filter((status): status is PastResourceStatus => status !== STATUS_ALL)
                      .map((status) => (
                        <SelectItem key={status} value={status}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isDocumentsLoading ? (
            <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/10 p-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading documents…
            </div>
          ) : isDocumentsError ? (
            <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed bg-muted/10 p-10 text-center text-sm text-muted-foreground">
              <FileStack className="h-10 w-10 text-muted-foreground/60" />
              <div className="space-y-2">
                <p>We couldn’t load the documents right now.</p>
                <Button variant="outline" size="sm" onClick={() => refetchDocuments()}>
                  Retry
                </Button>
              </div>
            </div>
          ) : visibleDocuments.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed bg-muted/10 p-10 text-center text-sm text-muted-foreground">
              <FileStack className="h-10 w-10 text-muted-foreground/60" />
              <div className="space-y-1">
                <p>No documents match your filters just yet.</p>
                <p className="text-xs">
                  Adjust your search or category to explore other resources shared by the administration.
                </p>
              </div>
            </div>
          ) : (
            <>
              {isDocumentsFetching ? (
                <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Refreshing documents…
                </div>
              ) : null}
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {visibleDocuments.map((document) => (
                  <article
                    key={document.id}
                    className="flex h-full flex-col gap-4 rounded-lg border bg-card p-4"
                  >
                    <div className="flex flex-col gap-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            {document.type === 'link' ? (
                              <LinkIcon className="h-4 w-4 text-primary" />
                            ) : (
                              <FileText className="h-4 w-4 text-primary" />
                            )}
                          </div>
                        </div>
                        <Badge variant="outline">{document.category}</Badge>
                      </div>
                      <h2 className="text-base font-semibold leading-tight">
                        {highlightMatch(document.title ?? '', highlightQuery)}
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {highlightMatch(document.description ?? '', highlightQuery)}
                      </p>
                      {document.tags?.length ? (
                        <div className="flex flex-wrap gap-2">
                          {document.tags.map((tag) => (
                            <Badge key={tag} variant="secondary">
                              #
                              {highlightMatch(tag, highlightQuery)}
                            </Badge>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                      {document.cpdEventName && (
                        <span className="flex items-center gap-1">
                          <CalendarDays className="h-3 w-3 text-primary" />
                          <span className="truncate">
                            CPD event: {highlightMatch(document.cpdEventName, highlightQuery)}
                          </span>
                        </span>
                      )}
                      <span>Published by {document.owner}</span>
                      <span>
                        Last updated {formatUpdatedAt(document.updatedAt ?? '')} · {document.size}
                      </span>
                      {document.status ? (
                        <div className="mt-auto flex items-center gap-2">
                          <Badge
                            variant={document.status.toLowerCase() === 'draft' ? 'outline' : 'successOutline'}
                          >
                            {document.status.charAt(0).toUpperCase() + document.status.slice(1)}
                          </Badge>
                        </div>
                      ) : null}
                    </div>
                    <div className="mt-auto flex items-center gap-2">
                      <Button className={cn('flex-1', !isAdmin && 'w-full')} onClick={() => openDocument(document)}>
                        View in app
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={() => handleEditDocument(document)}
                        >
                          Edit
                        </Button>
                      )}
                      {isAdmin && (
                        <ConfirmAlert
                          trigger={(
                            <Button
                              variant="destructive"
                              className="flex-1"
                              disabled={deletePastResourceMutation.isPending}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </Button>
                          )}
                          title="Delete this document?"
                          description="This cannot be undone. The document will be permanently removed from past resources."
                          isPending={deletePastResourceMutation.isPending && deletingId === document.id}
                          onConfirm={() => handleDeleteDocument(document)}
                        />
                      )}
                    </div>
                  </article>
                ))}
              </div>
              <div className="mt-6 flex flex-col items-center justify-between gap-4 text-xs text-muted-foreground sm:flex-row">
                <span>
                  {totalItems > 0
                    ? `Showing ${pageRange.start}–${pageRange.end} of ${totalItems} resources`
                    : `Showing ${visibleDocuments.length} resource${visibleDocuments.length === 1 ? '' : 's'}`}
                </span>
                {totalPages > 1 && (
                  <Pagination className="w-full sm:w-auto">
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          size="default"
                          className={cn(pageIndex === 0 && 'pointer-events-none opacity-50')}
                          onClick={(event) => {
                            event.preventDefault()
                            if (pageIndex > 0) {
                              setPageIndex(pageIndex - 1)
                            }
                          }}
                        />
                      </PaginationItem>
                      {pageNumbers.map((pageNumber, index) => {
                        const previous = pageNumbers[index - 1]
                        const showEllipsis = previous !== undefined && pageNumber - previous > 1
                        return (
                          <Fragment key={pageNumber}>
                            {showEllipsis ? (
                              <PaginationItem>
                                <PaginationEllipsis />
                              </PaginationItem>
                            ) : null}
                            <PaginationItem>
                              <PaginationLink
                                href="#"
                                isActive={pageNumber === pageIndex}
                                size="default"
                                onClick={(event) => {
                                  event.preventDefault()
                                  if (pageNumber !== pageIndex) {
                                    setPageIndex(pageNumber)
                                  }
                                }}
                              >
                                {pageNumber + 1}
                              </PaginationLink>
                            </PaginationItem>
                          </Fragment>
                        )
                      })}
                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          size="default"
                          className={cn(
                            (totalPages === 0 || pageIndex >= totalPages - 1) && 'pointer-events-none opacity-50',
                          )}
                          onClick={(event) => {
                            event.preventDefault()
                            if (totalPages === 0 || pageIndex >= totalPages - 1) {
                              return
                            }
                            setPageIndex(pageIndex + 1)
                          }}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(selectedDocument)} onOpenChange={(open) => !open && closeDocument()}>
        <DialogContent className="flex max-h-[95vh] w-full max-w-4xl flex-col overflow-hidden transition-all sm:max-w-3xl">
          {selectedDocument && (
            <>
              <DialogHeader className="space-y-2">
                <DialogTitle>{selectedDocument.title}</DialogTitle>
                <DialogDescription>{selectedDocument.description}</DialogDescription>
              </DialogHeader>
              <div className="flex flex-1 flex-col gap-4 overflow-y-auto pr-1 sm:pr-2">
                <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    {selectedDocument.type === 'link' ? (
                      <LinkIcon className="h-4 w-4 text-primary" />
                    ) : (
                      <FileText className="h-4 w-4 text-primary" />
                    )}
                    <span>{selectedDocument.size}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{selectedDocument.category}</Badge>
                    <Separator orientation="vertical" className="h-4" />
                    <span>Updated {formatUpdatedAt(selectedDocument.updatedAt ?? '')}</span>
                  </div>
                </div>
                {selectedDocument.cpdEventName && (
                  <div className="flex items-center gap-2 rounded-md bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                    <CalendarDays className="h-4 w-4 text-primary" />
                    <span>Linked CPD event: {selectedDocument.cpdEventName}</span>
                  </div>
                )}
                <div className="flex items-center justify-end gap-2 text-xs">
                  {isAdmin && (
                    <ConfirmAlert
                      trigger={(
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={deletePastResourceMutation.isPending}
                        >
                          <Trash2 className="mr-2 h-3.5 w-3.5" />
                          Delete document
                        </Button>
                      )}
                      title="Delete this document?"
                      description="This cannot be undone. The document will be permanently removed from past resources."
                      isPending={deletePastResourceMutation.isPending && deletingId === selectedDocument.id}
                      onConfirm={() => handleDeleteDocument(selectedDocument)}
                    />
                  )}
                  {(selectedDocument.preview?.kind === 'file' || selectedDocument.preview?.kind === 'link') && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const element = viewerRef.current
                        if (!element || typeof document === 'undefined') return
                        if (!document.fullscreenElement) {
                          element.requestFullscreen?.().catch(() => {
                            setIsFullscreenActive(false)
                          })
                        } else if (document.fullscreenElement === element) {
                          document.exitFullscreen?.().catch(() => {
                            setIsFullscreenActive(true)
                          })
                        } else {
                          element.requestFullscreen?.().catch(() => {
                            setIsFullscreenActive(false)
                          })
                        }
                      }}
                    >
                      {isFullscreenActive ? 'Exit full screen' : 'Full screen view'}
                    </Button>
                  )}
                </div>
                {selectedDocument.preview?.kind === 'file' && (
                  <InlineDocumentViewer
                    url={selectedDocument.preview?.url ?? ''}
                    mimeType={selectedDocument.preview?.mimeType ?? null}
                    title={selectedDocument.title}
                    className={cn(
                      isFullscreenActive
                        ? 'h-[calc(100vh-14rem)]'
                        : 'h-[60vh]',
                    )}
                    isFullscreen={isFullscreenActive}
                    onFullscreenExit={() => {
                      if (typeof document !== 'undefined' && document.fullscreenElement) {
                        document.exitFullscreen?.()
                      }
                    }}
                    ref={viewerRef}
                  />
                )}
                {selectedDocument.preview?.kind === 'link' && (
                  <InlineDocumentViewer
                    url={selectedDocument.preview?.url ?? ''}
                    title={selectedDocument.preview?.label ?? selectedDocument.title}
                    headerLabel={selectedDocument.preview?.label ?? undefined}
                    className={cn(
                      isFullscreenActive
                        ? 'h-[calc(100vh-14rem)]'
                        : 'h-[60vh]',
                    )}
                    isFullscreen={isFullscreenActive}
                    onFullscreenExit={() => {
                      if (typeof document !== 'undefined' && document.fullscreenElement) {
                        document.exitFullscreen?.()
                      }
                    }}
                    ref={viewerRef}
                  />
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
